<template>
  <div class="app-container">
    <b-overlay
      :show="loading"
      rounded
      opacity="0.8"
      spinner-variant="success"
      spinner-type="border"
      spinner-size="lg"
      ><div class="card card-custom mb-5">
        <div class="card-body">
          <!-- Calendar table -->
          <div class="calendar-container">
            <div class="calendar-wrapper">
              <table class="calendar-table">
                <colgroup>
                  <col class="week-column" />
                  <col
                    v-for="day in days"
                    :key="`col-${day.id}`"
                    class="day-column"
                  />
                </colgroup>
                <thead>
                  <tr class="header-row">
                    <th class="week-cell">Tuần</th>
                    <th v-for="day in days" :key="day.id" class="day-header">
                      {{ day.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="week in numberOfWeeks"
                    :key="week"
                    class="calendar-row"
                  >
                    <td class="week-cell">Tuần {{ week }}</td>
                    <td
                      v-for="day in days"
                      :key="`${week}-${day.id}`"
                      class="day-cell"
                    >
                      <div class="event-container">
                        <div
                          v-for="event in getEventsStartingOnDay(week, day.id)"
                          :key="event.id"
                          class="event-card"
                          :style="{
                            backgroundColor: event.color,
                            color: getTextColor(event.color),
                          }"
                        >
                          <div class="event-content">
                            <span class="event-title">{{ event.title }}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div></b-overlay
    >
  </div>
</template>

<script>
export default {
  props: {
    programId: {
      type: [String, Number],
      default: null,
    },
    programData: {
      type: Object,
      default: () => ({}),
    },
    viewMode: {
      type: String,
      default: 'day',
    },
    numberOfWeeks: {
      type: Number,
      default: 15,
    },
  },
  data() {
    return {
      events: [], // Will store the transformed data
      days: [
        { id: 2, name: 'Ngày 1' },
        { id: 3, name: 'Ngày 2' },
        { id: 4, name: 'Ngày 3' },
        { id: 5, name: 'Ngày 4' },
        { id: 6, name: 'Ngày 5' },
        { id: 7, name: 'Ngày 6' },
        { id: 8, name: 'Ngày 7' },
      ],
      // Using a single color for all events
      defaultColor: '#FF8A65',
      loading: false,
    };
  },
  methods: {
    getTextColor(backgroundColor) {
      if (
        !backgroundColor ||
        typeof backgroundColor !== 'string' ||
        !/^#[0-9A-Fa-f]{6}$/.test(backgroundColor)
      ) {
        return '#000000';
      }
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    // Get events for a specific week and day
    getEventsStartingOnDay(week, dayId) {
      return this.events.filter(
        (event) => event.week === week && event.startDay === dayId,
      );
    },

    async loadActivitiesByProgram(id) {
      try {
        this.loading = true;
        const response = await this.$api.get(
          `TranServicePackage/Activity/${id}`,
        );

        // Check the response structure and extract data
        const apiData = response.data || response || [];

        // Make sure apiData is an array before mapping
        if (!Array.isArray(apiData)) {
          console.error('API did not return an array:', apiData);
          this.events = [];
          return;
        }

        this.events = apiData
          .map((item) => {
            const startranAtranDay = Number(item.startranAtranDay);
            if (!Number.isInteger(startranAtranDay) || startranAtranDay < 2) {
              console.warn(
                `Invalid startAtDay for item ${item.id}: ${item.startranAtranDay}`,
              );
              return {
                id: item.id,
                week: 1,
                startDay: 2,
                title: item.activity ? item.activity.name : 'Untitled Activity',
                color: item.activity?.color ?? this.defaultColor,
              };
            }

            const adjustedStartAtDay = startranAtranDay - 2;
            const week = Math.floor(adjustedStartAtDay / 7) + 1;
            const dayOfWeekIndex = adjustedStartAtDay % 7;
            const adjustedDayOfWeek = dayOfWeekIndex + 2;

            return {
              id: item.id,
              week: week,
              startDay: adjustedDayOfWeek,
              title: item.activity ? item.activity.name : 'Untitled Activity',
              color: item.activity?.color ?? this.defaultColor,
            };
          })
          .filter((event) => event !== null);

        this.$set(
          this,
          'numberOfWeeks',
          Math.max(...this.events.map((event) => event.week), 1),
        );
      } catch (error) {
        console.error('Error loading activities:', error);
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.loadActivitiesByProgram(this.$route.params.id);
    } else if (this.programId) {
      this.loadActivitiesByProgram(this.programId);
    }
  },
};
</script>

<style scoped>
.app-container {
  padding: 0;
}

.calendar-container {
  width: 100%;
  position: relative;
}

.calendar-wrapper {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}

.calendar-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed; /* Quan trọng để đảm bảo các cột có chiều rộng cố định */
}

/* Định nghĩa chiều rộng cột */
.week-column {
  width: 80px;
}

.day-column {
  width: calc((100% - 80px) / 7); /* Chia đều không gian còn lại cho 7 ngày */
}

.header-row th {
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  white-space: nowrap;
}

.day-header {
  width: calc((100% - 80px) / 7); /* Đảm bảo header cũng chiếm đúng độ rộng */
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-cell {
  width: 80px;
  text-align: center;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
}

.day-cell {
  border: 1px solid #ddd;
  vertical-align: top;
  height: 80px;
  padding: 4px;
  width: calc((100% - 80px) / 7); /* Đồng bộ với chiều rộng của cột */
}

.event-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.event-card {
  padding: 4px 8px;
  font-size: 12px;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-bottom: 5px;
  user-select: none;
}

.event-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: 500;
}

/* Responsive styling */
@media (max-width: 992px) {
  .week-column {
    width: 60px;
  }

  .week-cell {
    width: 60px;
  }

  .day-column {
    width: calc((100% - 60px) / 7);
  }

  .day-cell {
    width: calc((100% - 60px) / 7);
    height: 60px;
  }

  .event-card {
    padding: 2px 4px;
    font-size: 10px;
  }
}
.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 9999;
}

.large-spinner {
  width: 4rem;
  height: 4rem;
}
::v-deep .b-overlay .spinner-border {
  width: 4rem !important;
  height: 4rem !important;
  border-width: 0.3em !important;
}
::v-deep .b-overlay {
  height: 100vh;
}
::v-deep .b-overlay > .position-absolute {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
